import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.has_profile ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative gradient-hero">
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl">NutriGuide</span>
          </Link>
          <div className="max-w-md">
            <h2 className="font-display text-3xl font-bold text-slate-900">Welcome back.</h2>
            <p className="mt-3 text-slate-600">Pick up where you left off — your personalized food list is waiting.</p>
          </div>
          <div></div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Sign in</h1>
            <p className="mt-2 text-sm text-slate-500">Enter your account details below.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="rounded-xl h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="rounded-xl h-11"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-full h-11 bg-emerald-600 hover:bg-emerald-700"
            data-testid="login-submit-btn"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-emerald-700 font-medium hover:underline" data-testid="login-forgot">
              Forgot password?
            </Link>
            <Link to="/register" className="text-slate-600 hover:underline" data-testid="login-to-register">
              Create account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
